<template>
  <div ref="componentRoot" class="mt-5">
    <!-- Select Community -->
    <v-select
      v-model="order.community"
      :items="communities"
      item-value="id"
      item-title="name"
      return-object
      variant="outlined"
      density="comfortable"
      label="Wählen Sie eine Gemeinde"
      @blur="touched.community = true"
    />
    <!-- Loading Spinner -->
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>
    <!-- Grouped Products -->
    <div v-if="groupedProducts.length && !isLoading" class="mb-6">
      <div class="media-table-wrapper">
        <v-simple-table class="media-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Produkt</th>
                <th v-if="hasPapierColumn" class="text-left">Papierplan</th>
                <th v-if="hasPdfColumn" class="text-left">PDF</th>
                <th v-if="hasDxfColumn" class="text-left">
                  Digitale Daten (DXF/DWG)
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Other Products -->
              <tr v-for="(productGroup, i) in groupedProducts" :key="i">
                <td>{{ productGroup.name }}</td>
                <!-- Papierplan -->
                <td v-if="hasPapierColumn" class="cell-layout">
                  <div v-if="productGroup.papier" class="cell-content">
                    <v-checkbox
                      v-model="productGroup.papier.selected"
                      :disabled="productGroup.papier.readonly"
                      class="checkbox"
                      color="primary"
                      dense
                      hide-details
                    />
                    <v-text-field
                      v-model.number="productGroup.papier.quantity"
                      type="number"
                      min="1"
                      density="comfortable"
                      variant="outlined"
                      hide-details
                      class="quantity-input"
                      @input="onQuantityChange(i)"
                    ></v-text-field>
                    <span class="price-right">
                      {{ productGroup.papier.totalPrice }}.-
                    </span>
                  </div>
                  <div v-if="productGroup.papier" class="ml-2 mr-2">
                    <div class="name">{{ productGroup.papier.name }}</div>
                    <div class="unit-price">
                      Ein Exemplar: {{ productGroup.papier.price }}.- <br />
                      jedes weitere Exemplar +5.-
                    </div>
                  </div>
                </td>
                <!-- PDF -->
                <td v-if="hasPdfColumn" class="cell-layout">
                  <div
                    v-for="(pdf, pdfIndex) in productGroup.pdf"
                    :key="pdfIndex"
                  >
                    <div class="cell-content">
                      <v-checkbox
                        v-model="pdf.selected"
                        color="primary"
                        class="checkbox"
                        dense
                        hide-details
                      />
                      <span class="price-right"> {{ pdf.price }}.- </span>
                    </div>
                    <div class="ml-2 mr-2">
                      <div class="name">{{ pdf.name }}</div>
                      <div class="unit-price">
                        {{ pdf.extra }}
                      </div>
                    </div>
                  </div>
                </td>
                <!-- Digitale Daten (DXF/DWG) -->
                <td v-if="hasDxfColumn" class="cell-layout">
                  <div v-if="productGroup.dxf" class="cell-content">
                    <v-checkbox
                      v-model="productGroup.dxf.selected"
                      color="primary"
                      class="checkbox"
                      dense
                      hide-details
                    />
                    <span class="price-right">
                      {{ productGroup.dxf.price }}.-
                    </span>
                  </div>
                  <div v-if="productGroup.dxf" class="ml-2 mr-2">
                    <div class="name">{{ productGroup.dxf.name }}</div>
                    <div class="unit-price">
                      {{ productGroup.dxf.extra }}
                    </div>
                  </div>
                </td>
              </tr>
              <!-- Grundpauschale -->
              <tr v-if="isFirstOrder">
                <td>Grundpauschale</td>
                <td :colspan="colspanCount" class="cell-layout">
                  <div class="cell-content">
                    <div class="checkbox-and-name">
                      <v-checkbox
                        v-model="grundpauschale.selected"
                        color="primary"
                        class="checkbox"
                        dense
                        hide-details
                        :disabled="true"
                      />
                      <span class="name">{{ grundpauschale.name }}</span>
                    </div>
                    <span class="price-right">
                      {{ grundpauschale.price }}.-
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>
    <!-- Address -->
    <v-text-field
      v-model="order.orderAddress"
      label="Adressobjekt"
      class="mb-2"
      variant="outlined"
      density="comfortable"
      clearable
      :error="touched.orderAddress && !!errors.orderAddress"
      :error-messages="errors.orderAddress"
      @blur="touched.orderAddress = true"
    />
    <!-- Parcel -->
    <v-text-field
      v-model="order.orderParcel"
      label="Grundstücks-/Katasternummer"
      density="comfortable"
      variant="outlined"
      :error="touched.orderParcel && !!errors.orderParcel"
      :error-messages="errors.orderParcel"
      @blur="touched.orderParcel = true"
    />
    <!-- Link to geoportal.ch with community name -->
    <div v-if="order.community" class="geoportal-link mb-4">
      <span>
        <a
          :href="getGeoportalLink(order.community)"
          target="_blank"
          class="primary--text text-decoration-none"
        >
          Zu {{ order.community.name }} auf dem Geoportal
        </a>
      </span>
    </div>
    <!-- File Upload -->
    <OrderFileUpload @updateFiles="handleFileUpload" />
    <!-- Action Buttons -->
    <div class="mt-5">
      <v-btn
        @click="$emit('removeOrder')"
        icon
        color="error"
        size="small"
        class="mr-2"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
      <v-btn @click="$emit('addOrder')" icon color="primary" size="small">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  watch,
  watchEffect,
  defineProps,
  defineEmits,
  computed,
  onMounted,
  onBeforeUnmount,
  defineExpose,
} from 'vue'
import axios from 'axios'
import OrderFileUpload from './OrderFileUpload.vue'

const props = defineProps({
  order: Object,
  communities: Array,
  isFirstOrder: Boolean,
})

const emit = defineEmits([
  'update:order',
  'removeOrder',
  'addOrder',
  'valid',
  'heightChange',
])

const componentRoot = ref(null)

// Custom debounce function
const debounce = (func, wait) => {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

const sendHeightUpdate = debounce(() => {
  const scrollHeight = componentRoot.value.scrollHeight
  const docHeight = document.documentElement.scrollHeight
  const sendingHeight = Math.max(scrollHeight, docHeight)
  emit('heightChange', sendingHeight)
}, 300)

defineExpose({
  sendHeightUpdate,
})

const setupResizeObserver = () => {
  const resizeObserver = new ResizeObserver(() => {
    sendHeightUpdate()
  })

  onMounted(() => {
    if (componentRoot.value) {
      resizeObserver.observe(componentRoot.value)
    }
  })
  onBeforeUnmount(() => {
    resizeObserver.disconnect()
    // Send a small height to parent, ensuring shrink detection
    emit('heightChange', 100)
  })
}

// Component logic
const order = reactive({ ...props.order })
const groupedProducts = ref([])
const errors = ref({})
const touched = ref({
  community: false,
  orderAddress: false,
  orderParcel: false,
})
const isLoading = ref(false)
const grundpauschale = reactive({
  name: 'Grundpauschale',
  price: 50,
  selected: true,
})

const hasPapierColumn = computed(() => {
  return groupedProducts.value.some(
    (product) => product.papier !== null && product.papier !== undefined,
  )
})

const hasPdfColumn = computed(() => {
  return groupedProducts.value.some(
    (product) => product.pdf && product.pdf.length > 0,
  )
})

const hasDxfColumn = computed(() => {
  return groupedProducts.value.some(
    (product) => product.dxf !== null && product.dxf !== undefined,
  )
})

const colspanCount = computed(() => {
  let count = 0
  if (hasPapierColumn.value) count++
  if (hasPdfColumn.value) count++
  if (hasDxfColumn.value) count++
  return count
})

const calculatePaperPrice = (productGroup, quantity) => {
  if (!productGroup.papier) return 0
  const additionalPrice = 5
  const quantityValue = quantity || 0
  const totalPrice =
    productGroup.papier.price + (quantityValue - 1) * additionalPrice
  productGroup.papier.totalPrice =
    quantityValue > 0 ? totalPrice : productGroup.papier.price
  return productGroup.papier.totalPrice
}

const validateForm = () => {
  const hasSelectedProduct = groupedProducts.value.some((group) => {
    return (
      (group.papier && group.papier.selected) ||
      (group.pdf && group.pdf.some((pdf) => pdf.selected)) ||
      (group.dxf && group.dxf.selected)
    )
  })
  const isAddressOrParcelFilled =
    order.orderAddress?.trim() || order.orderParcel?.trim()
  errors.value = {
    orderAddress:
      touched.value.orderAddress && !isAddressOrParcelFilled
        ? 'Adressobjekt oder Grundstücks-/Katasternummer ist erforderlich.'
        : '',
    orderParcel:
      touched.value.orderParcel && !isAddressOrParcelFilled
        ? 'Adressobjekt oder Grundstücks-/Katasternummer ist erforderlich.'
        : '',
  }
  const isValid =
    (!touched.value.community || !!order.community) &&
    isAddressOrParcelFilled &&
    hasSelectedProduct
  emit('valid', isValid)
  sendHeightUpdate()
}

const replaceSpecialChars = (name) => {
  return name
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/\s/g, '_')
    .replace(/-/g, '_')
    .toLowerCase()
    .trim()
}

const getGeoportalLink = (community) => {
  if (!community || !community.name) return '#'
  if (community.name === 'Wald') return `https://geoportal.ch/wald_zh`
  return `https://geoportal.ch/${replaceSpecialChars(community.name)}`
}

const onQuantityChange = (index) => {
  const productGroup = groupedProducts.value[index]
  if (productGroup.papier) {
    productGroup.papier.selected = true
    calculatePaperPrice(productGroup, productGroup.papier.quantity)
  }
  sendHeightUpdate()
}

const fetchProductsByCommunity = async (communityId) => {
  if (!communityId) return []
  try {
    const url = `${process.env.VUE_APP_ZKL_BACKEND_URL}/v1/services/tasks/${communityId}`
    const response = await axios.get(url)
    const rawProducts = response?.data?.products || []
    const transformed = rawProducts.map((product) => {
      const result = {
        name: product.name,
        papier: null,
        pdf: [],
        dxf: null,
      }
      product.description.forEach((desc) => {
        const entry = {
          name: desc.name,
          extra: desc.extra,
          selected: false,
          quantity: 1,
          price: desc.price,
        }
        if (desc.type === 'papier') {
          entry.totalPrice = desc.price
          result.papier = entry
        } else if (desc.type === 'pdf') {
          result.pdf.push(entry)
        } else if (desc.type === 'dxf') {
          result.dxf = entry
        }
      })
      return result
    })
    const desiredOrder = [
      'Katasterplan Amtliche Vermessung',
      'Auszug Plan für das Grundbuch',
      'Werkleitungsplan Abwasser',
      'Werkleitungsplan Wasser',
      'Werkleitungsplan Gas',
      'Werkleitungsplan Fernwärme',
    ]
    const sortedProducts = transformed.sort((a, b) => {
      const aIndex = desiredOrder.indexOf(a.name)
      const bIndex = desiredOrder.indexOf(b.name)
      return aIndex - bIndex
    })
    return sortedProducts
  } catch (error) {
    console.error('Error loading products:', error)
    return []
  }
}

const handleFileUpload = (files) => {
  console.log('Files received in handleFileUpload:', files)
  order.files = files
  console.log('Updated files list:', order.files)
  emit('update:order', { ...order })
  sendHeightUpdate()
}

watchEffect(() => {
  const selectedProducts = []
  let werkleitungsplanDxfCount = 0
  groupedProducts.value.forEach((productGroup) => {
    if (productGroup.papier && productGroup.papier.selected) {
      selectedProducts.push({
        groupName: productGroup.name,
        type: 'papier',
        name: productGroup.papier.name,
        quantity: productGroup.papier.quantity,
        price: productGroup.papier.totalPrice,
      })
    }
    productGroup.pdf?.forEach((pdf) => {
      if (pdf.selected) {
        selectedProducts.push({
          groupName: productGroup.name,
          type: 'pdf',
          name: pdf.name,
          price: pdf.price,
        })
      }
    })
    if (productGroup.dxf && productGroup.dxf.selected) {
      if (productGroup.name.includes('Werkleitungsplan')) {
        werkleitungsplanDxfCount++
      }
      selectedProducts.push({
        groupName: productGroup.name,
        type: 'dxf',
        name: productGroup.dxf.name,
        price: productGroup.dxf.price,
      })
    }
  })
  if (props.isFirstOrder && grundpauschale.selected) {
    selectedProducts.push({
      groupName: grundpauschale.name,
      type: 'grundpauschale',
      name: grundpauschale.name,
      price: grundpauschale.price,
    })
  }
  const adjustedSelectedProducts = selectedProducts.map((product) => {
    if (
      product.type === 'dxf' &&
      product.groupName.includes('Werkleitungsplan')
    ) {
      if (werkleitungsplanDxfCount-- > 2) {
        return { ...product, price: 0 }
      }
    }
    return product
  })
  order.selectedProducts = adjustedSelectedProducts
  emit('update:order', { ...order })
  validateForm()
})

watch(
  () => order.community,
  async (newCommunity) => {
    if (newCommunity?.id) {
      isLoading.value = true
      groupedProducts.value = await fetchProductsByCommunity(newCommunity.id)
      isLoading.value = false
    } else {
      groupedProducts.value = []
    }
    validateForm()
    emit('update:order', { ...order })
  },
  { immediate: true },
)

watch(
  () => [order.orderAddress, order.orderParcel],
  () => {
    validateForm()
    emit('update:order', { ...order })
  },
  { deep: true },
)

watch(
  () => groupedProducts.value,
  () => {
    validateForm()
  },
  { deep: true },
)

onMounted(() => {
  setupResizeObserver()
  sendHeightUpdate()
})
</script>

<style scoped>
/* Your existing styles */
.checkbox-and-name {
  display: flex;
  align-items: center;
}
.checkbox-and-name .v-checkbox {
  margin-right: 8px;
}
.file-upload-wrapper {
  position: relative;
  margin-bottom: 16px;
}
.optional-text {
  color: #666;
  font-size: 0.875rem;
  position: absolute;
  left: 12px;
  bottom: -20px;
  display: flex;
  align-items: center;
}
.optional-text .v-icon {
  margin-right: 4px;
}
.geoportal-link {
  margin-top: 10px;
  margin-bottom: 10px;
}
.geoportal-link a {
  color: inherit;
  text-decoration: none;
}
.geoportal-link a:hover {
  text-decoration: underline;
}
.cell-content {
  display: flex;
  align-items: center;
  margin: 8px 0;
  position: relative;
}
.cell-layout {
  padding: 8px;
  position: relative;
}
.quantity-input {
  width: 60px;
  margin-left: 8px;
}
.name {
  text-align: left;
  margin: 4px 0;
}
.price-right {
  margin-left: 20px;
}
.unit-price {
  margin-top: 8px;
  font-size: 0.875rem;
  color: #666;
}
:deep(.v-input--selection-controls) {
  margin: 4px 0;
  padding: 0;
}
/* Ensure the table uses the full width */
.media-table-wrapper {
  width: 100%;
  overflow-x: auto;
}
.media-table {
  width: 100%;
  table-layout: fixed; /* Ensures the table respects the width settings */
}
.media-table table {
  width: 100%;
}
.media-table th,
.media-table td {
  padding: 12px;
  text-align: left;
}
/* Ensure columns use full width */
.media-table th:nth-child(1),
.media-table td:nth-child(1) {
  width: 30%; /* Adjust based on your needs */
}
.media-table th:nth-child(2),
.media-table td:nth-child(2) {
  width: 20%; /* Adjust based on your needs */
}
.media-table th:nth-child(3),
.media-table td:nth-child(3) {
  width: 25%; /* Adjust based on your needs */
}
.media-table th:nth-child(4),
.media-table td:nth-child(4) {
  width: 25%; /* Adjust based on your needs */
}
/* Ensure the layout changes for mobile devices */
@media (max-width: 768px) {
  .cell-content {
    flex-direction: column;
    align-items: flex-start;
    margin: 10px 0;
  }
  .v-checkbox {
    margin: 8px 0;
  }
  .quantity-input {
    width: 100%;
    margin: 8px 0;
  }
  .price-right {
    margin: 8px 0;
  }
  .cell-layout > div {
    margin: 8px 0;
  }
  .media-table td {
    padding: 8px;
  }
}
</style>
